import { getSurveys as apiGetSurveys, getSurveyDetail as apiGetSurveyDetail, ApiError } from './apiService';

/* ------------------------------------------------------------------ */
/* Caché local (offline-first)                                        */
/* ------------------------------------------------------------------ */
/*
 * Las encuestas (lista + detalle con preguntas) se guardan en
 * localStorage apenas se cargan con éxito. Si una petición falla por
 * falta de conexión (`ApiError` con `status: 0`), se usa lo último
 * guardado para que el operativo pueda seguir viendo/llenando
 * formularios sin internet. Si la petición falla por otra razón
 * (401, 500, etc.), el error se propaga normalmente.
 */

const LIST_CACHE_KEY = 'pulso17.surveys.cache.list';
const DETAIL_CACHE_PREFIX = 'pulso17.surveys.cache.detail.';

function isOffline(err) {
  return err instanceof ApiError && err.status === 0;
}

function readCache(key) {
  try {
    const raw = localStorage.getItem(key);
    return raw ? JSON.parse(raw) : null;
  } catch {
    return null;
  }
}

function writeCache(key, value) {
  try {
    localStorage.setItem(key, JSON.stringify(value));
  } catch {
    // Si localStorage está lleno o no disponible, seguimos sin caché.
  }
}

function cacheList(surveys) {
  writeCache(LIST_CACHE_KEY, surveys);
}

function getCachedList() {
  return readCache(LIST_CACHE_KEY) ?? [];
}

function cacheDetail(survey) {
  writeCache(`${DETAIL_CACHE_PREFIX}${survey.id}`, survey);
  // También refresca la entrada de esta encuesta dentro de la lista
  // cacheada, para que "Formularios" y la captura no queden desincronizados.
  const list = getCachedList();
  const { questions, ...summary } = survey; // eslint-disable-line no-unused-vars
  const next = list.some((s) => s.id === survey.id)
    ? list.map((s) => (s.id === survey.id ? summary : s))
    : [...list, summary];
  cacheList(next);
}

function getCachedDetail(id) {
  return readCache(`${DETAIL_CACHE_PREFIX}${String(id)}`) ?? null;
}


/**
 * Encuesta ya normalizada para la interfaz (id como string, nombres de
 * campo en camelCase). La API real (`GET /v1/surveys`) entrega `name`,
 * `start_date`, `end_date`, etc.; acá se traduce una sola vez para que
 * el resto de la app no tenga que preocuparse por el formato original.
 *
 * @typedef {Object} Survey
 * @property {string} id
 * @property {string} title
 * @property {string} description
 * @property {number} phase
 * @property {string} version
 * @property {boolean} active
 * @property {string|null} startDate fecha ISO `YYYY-MM-DD`
 * @property {string|null} endDate fecha ISO `YYYY-MM-DD`
 * @property {string} createdAt fecha-hora tal como la entrega la API
 */
function normalizeSurvey(survey) {
  return {
    id: String(survey.id),
    title: survey.name,
    description: survey.description,
    phase: survey.phase,
    version: survey.version,
    active: Boolean(survey.active),
    startDate: survey.start_date,
    endDate: survey.end_date,
    createdAt: survey.created_at,
  };
}

/**
 * Opción de una pregunta de selección.
 * @typedef {Object} SurveyOption
 * @property {string} id
 * @property {string} text
 * @property {string} value
 * @property {number} order
 */
function normalizeOption(option) {
  return {
    id: String(option.id),
    text: option.text,
    value: option.value,
    order: option.order,
  };
}

/**
 * Pregunta ya normalizada, lista para que `SurveyQuestionField` la
 * renderice sin tener que interpretar `config` (que en la API es un
 * objeto libre que cambia según el tipo de pregunta).
 *
 * @typedef {Object} SurveyQuestion
 * @property {string} id
 * @property {number} order
 * @property {string} text
 * @property {'single_choice'|'short_text'|'long_text'|'image'} type
 * @property {boolean} required
 * @property {number} block sección/página del formulario a la que pertenece
 * @property {string} placeholder
 * @property {string} inputType para `short_text`: 'text' | 'tel' | 'email' | ...
 * @property {number|null} maxLength para `short_text`
 * @property {string} [accept] mime types aceptados, para `image`
 * @property {number|null} [maxSizeMb] para `image`
 * @property {{ questionOrder: number, value: string }|null} dependsOn
 * @property {SurveyOption[]} options
 */
function normalizeQuestion(question) {
  const config = question.config || {};
  return {
    id: String(question.id),
    order: question.order,
    text: question.text,
    type: question.type,
    required: Boolean(question.required),
    block: config.block ?? 1,
    placeholder: config.placeholder ?? '',
    inputType: config.input_type ?? 'text',
    maxLength: config.max_length ?? null,
    accept: config.accept ?? (question.type === 'image' ? 'image/*' : undefined),
    maxSizeMb: config.max_size_mb ?? null,
    dependsOn: config.depends_on
      ? { questionOrder: config.depends_on.question_order, value: config.depends_on.value }
      : null,
    options: Array.isArray(question.options)
      ? [...question.options].sort((a, b) => a.order - b.order).map(normalizeOption)
      : [],
  };
}

function normalizeSurveyDetail(survey) {
  const questions = Array.isArray(survey.questions)
    ? [...survey.questions].sort((a, b) => a.order - b.order).map(normalizeQuestion)
    : [];
  return { ...normalizeSurvey(survey), questions };
}

/** Todas las encuestas disponibles (sin preguntas), ya normalizadas. */
export async function getSurveys() {
  try {
    const response = await apiGetSurveys();
    const rows = Array.isArray(response?.data) ? response.data : [];
    const surveys = rows.map(normalizeSurvey);
    cacheList(surveys);
    return surveys;
  } catch (err) {
    if (isOffline(err)) return getCachedList();
    throw err;
  }
}

/**
 * Una encuesta puntual, con sus preguntas y opciones ya listas para
 * construir el formulario de captura. Devuelve `null` si la API
 * responde 404 (encuesta inexistente); cualquier otro error se
 * propaga para que la vista lo muestre. Si no hay conexión, cae a la
 * última versión guardada localmente (si existe).
 *
 * @param {string|number} id
 * @returns {Promise<(Survey & { questions: SurveyQuestion[] })|null>}
 */
export async function getSurveyById(id) {
  try {
    const response = await apiGetSurveyDetail(id);
    if (!response?.data) return null;
    const survey = normalizeSurveyDetail(response.data);
    cacheDetail(survey);
    return survey;
  } catch (err) {
    if (err instanceof ApiError && err.status === 404) return null;
    if (isOffline(err)) {
      const cached = getCachedDetail(id);
      if (cached) return cached;
    }
    throw err;
  }
}

/** `true` si la encuesta con este id tiene una copia guardada localmente. */
export function hasCachedSurvey(id) {
  return getCachedDetail(id) !== null;
}

